import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import Button from '@rio-cloud/rio-uikit/Button';
import SimpleTooltip from '@rio-cloud/rio-uikit/SimpleTooltip';

import type { Importance, Notification, State } from '../services/notificationsTypes';
import { FormattedTimestamp } from './FormattedTimestamp';
import { PostMessageType } from './Notifications';
import { gaPush, TRACKING_CATEGORIES } from '../../configuration/setup/googleAnalytics';
import { useRunsInLivemonitor } from './notificationSlice';

const getImportanceIconClassName = (importance: Importance) => {
    switch (importance) {
        case 'HIGH':
            return 'rioglyph rioglyph-error-sign text-color-danger';
        case 'MEDIUM':
            return 'rioglyph rioglyph-warning-sign text-color-warning';
        default:
            return '';
    }
};

const isLivemonitorLink = (link: string) => link.startsWith('https://livemonitor.rio.cloud');

// Replace the link to the livemonitor to use the new event details view of the event-monitor Frontend. Use the event id later in the items component
// and strip away the asset ID.
// "https://livemonitor.rio.cloud/#map/8eb85b6d-f0c2-4cc6-8d03-69856be8bb0e?eventId=6e488ec1-5728-41cf-b1e1-68e94f42dbe3&assetIds=8eb85b6d-f0c2-4cc6-8d03-69856be8bb0e&lock=true",
// https://event-monitor.rio.cloud/#/eventDetails/6e488ec1-5728-41cf-b1e1-68e94f42dbe3
const replaceNotificationLink = (link: string, runsInLivemonitorSidebar: boolean) => {
    if (!isLivemonitorLink(link)) {
        return link;
    }

    // Replace the livemonitor link in case this application runs inside the livemonitor but in the header,
    // open asset details sidebar with notifications tab
    if (!runsInLivemonitorSidebar) {
        return `${link}&tabId=6`; // tabId 6 is the notifications tab of the asset details sidebar
    }

    const [, queryString] = link.split('?');
    if (!queryString) {
        return link;
    }

    const eventId = new URLSearchParams(queryString).get('eventId');
    return eventId ? `https://event-monitor.rio.cloud/#/eventDetails/${eventId}` : link;
};

type NotificationItemNewProps = {
    notification: Notification;
    isVehicleMode?: boolean;
    onUpdateNotification: (notification: Notification) => void;
};

// FIXME: don't open new tab when already in livemonitor

export const NotificationItemNew = (props: NotificationItemNewProps) => {
    const { notification, isVehicleMode = false, onUpdateNotification } = props;

    const { title, message, state, importance, createdAt, link: orgLink, sourceEntityName } = notification;

    const runsInLivemonitor = useRunsInLivemonitor();

    // Replace link to point to event details page ONLY when being already inside the Livemonitor sidebar.
    // This points the user to the livemonitor when opening the bell icon in the header from within another service,
    // since we cannot redirect the fleet user to the event details page where he will see an error for not having enough rights.
    // Being inside the Livemonitor sidebar, it will direct the user to the event details to avoid a page reload.
    // Being inside the Livemonitor header bell icon, it will still redirect to the Livemonitor but with notifications tab open.
    // TODO: replace this workaround, when the event details view allows fleet user to read the event details. For now, only the fleet admin can.
    const runsInLivemonitorSidebar = window.location.pathname.includes('vehicleNotifications');
    const link = orgLink ? replaceNotificationLink(orgLink, runsInLivemonitorSidebar) : orgLink;

    const [isHover, setIsHover] = useState(false);

    const handleNotificationClick = () => {
        if (link) {
            gaPush({
                category: TRACKING_CATEGORIES.NOTIFICATION,
                label: 'Notification item',
                action: 'Opened notification link',
            });

            // When embedded in the notification bell in the header, close the popover
            window.postMessage({ type: PostMessageType.NOTIFICATIONS_POPOVER_CLOSED }, '*');

            if (window) {
                // Open Livemonitor links in the same window when already inside Livemonitor,
                // otherwise open in a new tab.
                const target = runsInLivemonitor && isLivemonitorLink(link) ? '_top' : '_blank';
                window.open(link, target);
            }
        }
    };

    const handleNotificationLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        handleNotificationClick();
    };

    const updateSingleNotification = (readState: State) => {
        const updatedNotification = { ...notification, state: readState };
        onUpdateNotification(updatedNotification);
    };

    const handleMarkAsRead = (event: React.MouseEvent) => {
        event.stopPropagation();
        gaPush({
            category: TRACKING_CATEGORIES.NOTIFICATION,
            label: 'Notification item',
            action: 'Marked as read',
        });
        updateSingleNotification('READ');
    };

    const handleMarkAsUnread = (event: React.MouseEvent) => {
        event.stopPropagation();
        gaPush({
            category: TRACKING_CATEGORIES.NOTIFICATION,
            label: 'Notification item',
            action: 'Marked as unread',
        });
        updateSingleNotification('NEW');
    };

    const isNew = state === 'NEW';

    const notificationClassNames = classNames(
        'bg-white border rounded',
        'padding-x-15 padding-y-10',
        'user-select-none',
        link && 'cursor-pointer',
        isNew && !isHover && 'shadow-subtle',
        !isNew && !isHover && 'opacity-40',
        !isNew && isHover && 'border-style-dashed',
        isHover && 'shadow-default bg-decent'
    );

    // Reset link styling for notifications with a link.
    // The reason why we use a link is, to see the URL in the browser.
    const notificationLinkClassNames = classNames('reset-link');

    const readButtonClasses = classNames('position-absolute top-5 right-5');

    const titleClassNames = classNames(
        'text-medium',
        link && 'margin-right-20',
        link && isHover && 'text-decoration-underline underline-offset-4'
    );

    const notificationBody = (
        <div className='display-flex gap-10'>
            {importance !== 'LOW' && (
                <div className='text-size-18 margin-top--2 margin-left--2'>
                    <span className={getImportanceIconClassName(importance)} />
                </div>
            )}
            <div>
                <div className={titleClassNames}>
                    {!isVehicleMode && sourceEntityName && <span>{`${sourceEntityName}: `}</span>}
                    <span>{title}</span>
                    {link && isHover && (
                        <span className='rioglyph rioglyph-new-window rioglyph-align-baseline margin-left-5' />
                    )}
                </div>
                <div className='text-size-12 text-color-dark'>
                    <FormattedTimestamp timestamp={createdAt} />
                </div>
                {message && (
                    <div className='margin-top-5 line-height-125rel' data-testid='notification-message'>
                        {message}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div
            className='position-relative'
            data-testid='notification-list-item'
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
        >
            {link && (
                <a
                    href={link}
                    className={`${notificationClassNames} ${notificationLinkClassNames}`}
                    onClick={handleNotificationLinkClick}
                >
                    {notificationBody}
                </a>
            )}
            {!link && (
                <div className={notificationClassNames} onClick={handleNotificationClick}>
                    {notificationBody}
                </div>
            )}
            {isNew && (
                <SimpleTooltip content={<FormattedMessage id='intl-msg:notifications.markAsRead' />} placement='left'>
                    <Button
                        bsStyle='default'
                        variant='link'
                        bsSize='sm'
                        iconName='rioglyph-envelope-indicator text-size-16'
                        className={readButtonClasses}
                        onClick={handleMarkAsRead}
                        iconOnly
                    />
                </SimpleTooltip>
            )}
            {!isNew && isHover && (
                <SimpleTooltip content={<FormattedMessage id='intl-msg:notifications.markAsUnread' />} placement='left'>
                    <Button
                        bsStyle='default'
                        variant='link'
                        bsSize='sm'
                        iconName='rioglyph-envelope-open text-size-16'
                        className={readButtonClasses}
                        onClick={handleMarkAsUnread}
                        iconOnly
                    />
                </SimpleTooltip>
            )}
        </div>
    );
};
